import { AssessmentType, UserProfileRole } from '@@types/api.types';
import { RadarChartComponent } from '@components/shared/radar-chart.component';
import { RadarModal } from '@components/shared/radar-modal.component';
import { Tile } from '@components/shared/tile.component';
import { useDraftSurveyState } from '@hooks/surveys.hook';
import { useGetTechnologyUsesWithAssessments } from '@queries/technology-uses.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { ZoomInIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useReadinessTab } from '../../shared/use-readiness-tab';
import { StartTRLModal } from '../../technology-start-rapid-trl-modal.component';

type DomainPerformanceRadarTileProps = {
  technologyId: string;
};

export const DomainPerformanceRadarTile: FC<DomainPerformanceRadarTileProps> = (props) => {
  const { technologyId } = props;
  const { selectAssessment } = useReadinessTab({ assessmentIdParam: 'assessmentId', tabParam: 'trlTab' });

  const { data: technology } = useGetTechnology(technologyId);
  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();
  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const canEdit = user?.profile?.role === UserProfileRole.INNOVATOR && !isChildOrganizationView;

  const [isOpenFullScreenModal, setIsOpenFullScreenModal] = useState(false);
  const [trlModalOpen, setTrlModalOpen] = useState(false);
  const { data: technologyUses, isPending: isTechnologyUsesPending } = useGetTechnologyUsesWithAssessments(
    technology?.id,
  );
  const { emptyStateMessages, handleResumeSurvey, hasDraftAssessment, isDraftSurveysPending } = useDraftSurveyState(
    AssessmentType.TRL,
  );

  const allNodes =
    technologyUses
      ?.flatMap((use, useIndex) =>
        (use.domains ?? []).map((domain, domainIndex) => ({
          assessmentId: domain.featuredAssessment?.id ?? null,
          fullName: domain.name,
          isPrimary: useIndex === 0 && domainIndex === 0,
          label: domain.name?.split(' ')[0] || 'Unknown',
          value: domain.featuredAssessment?.score || 0,
        })),
      )
      .filter((node) => node.value > 0) ?? [];

  // Remove duplicates by keeping the node with the highest score for each label
  const nodes = allNodes.reduce((uniqueNodes: typeof allNodes, currentNode) => {
    const existingNodeIndex = uniqueNodes.findIndex((node) => node.label === currentNode.label);

    if (existingNodeIndex >= 0) {
      if (currentNode.value > uniqueNodes[existingNodeIndex].value) {
        uniqueNodes[existingNodeIndex] = currentNode;
      }
    } else {
      uniqueNodes.push(currentNode);
    }

    return uniqueNodes;
  }, []);

  if (nodes.length !== 0) {
    while (nodes.length < 4) {
      nodes.push({
        assessmentId: null,
        fullName: 'TRL: ' + nodes.length,
        isPrimary: false,
        label: 'TRL: ' + nodes.length,
        value: 0,
      });
    }
  }

  const handleNodeClick = (node: { assessmentId?: string | null }) => {
    if (node.assessmentId) {
      selectAssessment(node.assessmentId);
    }
  };

  const handleEmptyStateClick = () => {
    if (hasDraftAssessment) {
      handleResumeSurvey();
    } else {
      setTrlModalOpen(true);
    }
  };

  return (
    <>
      <Tile
        className="min-h-[392px] h-full"
        emptyState={{
          message: canEdit ? emptyStateMessages.message : undefined,
          onClick: canEdit ? handleEmptyStateClick : undefined,
          title: 'Domain Performance Unavailable',
        }}
        header={{
          iconButton: {
            icon: <ZoomInIcon />,
            onClick: () => setIsOpenFullScreenModal(true),
          },
          title: 'TRL Domain Performance',
        }}
        hoverReveal
        isEmpty={nodes.length === 0}
        isLoading={isTechnologyUsesPending || isDraftSurveysPending}
      >
        {nodes.length > 0 && (
          <div className="mt-[15px] flex w-full items-center justify-center">
            <RadarChartComponent
              nodes={nodes}
              onNodeClick={handleNodeClick}
            />
          </div>
        )}
      </Tile>
      {isOpenFullScreenModal && (
        <RadarModal
          isOpen={isOpenFullScreenModal}
          onClose={() => setIsOpenFullScreenModal(false)}
          title="TRL Domain Performance"
        >
          {({ labelFontSize, size }) => (
            <RadarChartComponent
              labelFontSize={labelFontSize}
              layout="full-screen"
              nodes={nodes}
              onNodeClick={handleNodeClick}
              size={size}
            />
          )}
        </RadarModal>
      )}
      <StartTRLModal
        isOpen={trlModalOpen}
        onClose={() => setTrlModalOpen(false)}
        technology={technology}
      />
    </>
  );
};
